import React, { useState, useEffect } from 'react';
import { Modal, Toast, Layout, Typography, Select, Button } from '@douyinfe/semi-ui';
import PageInfoForm from '../../components/H5Module/PageInfoForm';
import { IconEyeOpened, IconSave } from '@douyinfe/semi-icons';
import { useNavigate } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import H5Module from '../../components/H5Module';
import DroppableCanvas from '../../components/H5Module/DroppableCanvas';
import { getModuleConfig, getModuleDefaultProps } from '../../components/H5Module/moduleTypes';
import PropertyEditor from '../../components/H5Module/PropertyEditor';
import styles from './index.module.scss';
import { MODULE_TYPES } from '../../components/H5Module/moduleTypes';
import request from '../../utils/request';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const deviceList = [
    { value: 'iphone15promax', label: 'iPhone 15 Pro Max (430×932)', width: 430, height: 932 },
    { value: 'iphone15', label: 'iPhone 15 (393×852)', width: 393, height: 852 },
    { value: 'iphone14promax', label: 'iPhone 14 Pro Max (430×932)', width: 430, height: 932 },
    { value: 'pixel8pro', label: 'Pixel 8 Pro (411×899)', width: 411, height: 899 },
    { value: 'samsungs23ultra', label: 'Samsung S23 Ultra (400×872)', width: 400, height: 872 }
];
const scaleOptions = [
    { value: 0.5, label: '50%' },
    { value: 0.75, label: '75%' },
    { value: 1, label: '100%' },
    { value: 1.25, label: '125%' },
    { value: 1.5, label: '150%' }
];

const H5Editor = () => {
    const navigate = useNavigate();
    const [selectedModule, setSelectedModule] = useState(null);
    const [modules, setModules] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(deviceList[0]);
    const [scale, setScale] = useState(1);
    const [pageInfo, setPageInfo] = useState({
        title: '',
        typ: '',
        sort: 0,
        status: 0,
        public_uid: '0'
    });
    const pageInfoRef = React.useRef(pageInfo);
    useEffect(() => {
        pageInfoRef.current = pageInfo;
    }, [pageInfo])

    const [pageUid, setPageUid] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const uid = params.get('uid');
        if (uid) {
            fetchPageDetail(uid);
        } else {
            // 如果没有uid，尝试从本地存储加载草稿
            const draft = localStorage.getItem('h5editor_draft');
            if (draft) {
                try {
                    const draftData = JSON.parse(draft);
                    setPageInfo(draftData.pageInfo);
                    setModules(draftData.modules);
                    if (draftData.modules.length > 0) {
                        Toast.info('已加载未保存的草稿');
                    }
                } catch (e) {
                    console.error('解析草稿数据失败:', e);
                }
            }
        }
    }, []);

    const fetchPageDetail = async (uid) => {
        try {
            const response = await request.get(`/go/sp/all/get_all_page/`, {
                params: { uid }
            });
            console.log("response", response)
            if (response.errno === 0 && response.data) {
                setPageUid(uid);
                setPageInfo({
                    title: response.data.title || '',
                    typ: response.data.typ || '',
                    sort: response.data.sort || 0,
                    status: response.data.status || 0,
                    public_uid: response.data.public_uid || '0',
                    update_time: response.data.update_time || ''
                });
                if (response.data.content) {
                    try {
                        const content = JSON.parse(response.data.content);
                        if (content.ms) {
                            setModules(content.ms);
                        }
                    } catch (e) {
                        console.error('解析页面内容失败:', e);
                    }
                }
            }
        } catch (error) {
            console.error('获取页面详情失败:', error);
            Toast.error('获取页面详情失败');
        }
    };

    const handleDrop = (item) => {
        const newModule = {
            id: Date.now(),
            type: item.type,
            data: getDefaultData(item.type),
            title: getModuleConfig(item.type)?.title,
            style: {}
        };

        setModules(prev => {
            const bottomButtonIndex = prev.findIndex(m => m.type === 'BOTTOM_BUTTON');
            const newModules = [...prev];
            if (bottomButtonIndex !== -1) {
                newModules.splice(bottomButtonIndex, 0, newModule);
            } else {
                newModules.push(newModule);
            }
            return newModules;
        });
    };

    // 自动保存草稿
    useEffect(() => {
        if (!pageUid) { // 只在创建新页面时自动保存草稿
            const draftData = {
                pageInfo,
                modules
            };
            localStorage.setItem('h5editor_draft', JSON.stringify(draftData));
        }
    }, [pageInfo, modules]);

    const handleSave = async () => {
        if (modules.length === 0) {
            Toast.error('页面内容不能为空');
            return;
        }

        if (!pageInfoRef.current.title) {
            Modal.info({
                title: '页面信息',
                content: <PageInfoForm key="form1" pageInfo={pageInfo} onChange={setPageInfo} />,
                //icon: null,
                //footer: null,
                onOk: () => {
                    console.log("after page info", pageInfo, pageInfoRef.current)
                    if (pageInfoRef.current.title) {
                        handleSave();
                    }
                },
                width: 500
            });
            return;
        }

        const content = JSON.stringify({ ms: modules });
        const saveData = {
            //...pageInfo,
            ...pageInfoRef.current,
            content
        };

        try {
            let response;
            if (pageUid) {
                response = await request.post('/go/sp/all/edit_all_page/', {
                    ...saveData,
                    uid: pageUid
                });
            } else {
                response = await request.post('/go/sp/all/add_all_page/', saveData);
            }

            if (response.errno === 0) {
                Toast.success('保存成功');
                if (!pageUid && response.uid) {
                    // 保存成功后清除草稿
                    localStorage.removeItem('h5editor_draft');
                    setPageUid(response.uid);
                    navigate(`/h5editor?uid=${response.uid}`);
                }
            } else {
                Toast.error(response.errstr || '保存失败');
            }
        } catch (error) {
            console.error('保存失败:', error);
            Toast.error('保存失败');
        }
    };

    const getDefaultData = (type) => {
        return getModuleDefaultProps(type);
    };

    const handleDeviceChange = (value) => {
        const device = deviceList.find(d => d.value === value);
        setSelectedDevice(device);
    };

    const updateModule = (moduleId, updates) => {
        console.log("update model ", updates)
        setModules(prev => prev.map(m =>
            m.id === moduleId ? { ...m, ...updates } : m
        ));
        if (moduleId == selectedModule.id) {
            setSelectedModule({ ...selectedModule, ...updates })
        }
    };

    const handleStyleChange = (style) => {
        if (!selectedModule) return;
        updateModule(selectedModule.id, style);
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const onModalChange = (v) => {
        console.log("change", v)
        const newPageInfo = { ...v }
        setPageInfo(newPageInfo)
    }
    const onModalOk = () => {
        setPageInfo((prevPageInfo) => {
            const newPageInfo = { ...prevPageInfo }
            return newPageInfo
        })

        console.log("wait on ok", pageInfo, pageInfoRef.current)
        if (pageUid && modules.length > 0 && pageInfoRef.current.title) {
            handleSave();
        }
    }

    return (
        <DndProvider backend={HTML5Backend}>
            <Layout className={styles.editorContainer}>
                <Sider className={styles.leftSider}>
                    <div className={styles.moduleList}>
                        <div style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div>
                                    <Text
                                        strong
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            Modal.info({
                                                title: '页面信息',
                                                content: (
                                                    <PageInfoForm key="form2" pageInfo={pageInfo} onChange={onModalChange} />
                                                ),
                                                onOk: onModalOk,
                                                hasCancel: false,
                                                width: 500,
                                            });
                                        }}
                                    >
                                        {pageInfo.title || '未命名页面'}
                                    </Text>
                                    {pageInfo.update_time && (
                                        <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)', marginTop: '4px' }}>
                                            最后更新：{formatTimestamp(pageInfo.update_time)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 16px 16px' }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <Button
                                    icon={<IconEyeOpened />}
                                    disabled={!pageUid}
                                    onClick={() => {
                                        const token = localStorage.getItem('token');
                                        const sp_uid = token ? JSON.parse(token).s : '';
                                        const previewUrl = `/preview/h5.html?uid=${pageUid}&sp_uid=${sp_uid}`;
                                        window.open(previewUrl, '_blank');
                                    }}
                                    tooltip={!pageUid ? '请先保存页面后再预览' : '预览页面'}
                                >预览</Button>
                                <Button type="primary" icon={<IconSave />} onClick={() => {
                                    handleSave();
                                }}>保存</Button>
                            </div>
                        </div>
                        <div className={styles.modules}>
                            {Object.values(MODULE_TYPES).map(moduleConfig => (
                                <H5Module
                                    key={moduleConfig.type}
                                    type={moduleConfig.type}
                                    title={moduleConfig.title}
                                    onDrop={handleDrop}
                                />
                            ))}
                        </div>
                    </div>
                </Sider>
                <Content className={styles.content}>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                        <Select
                            className={styles.deviceSelector}
                            value={selectedDevice.value}
                            onChange={handleDeviceChange}
                            optionList={deviceList}
                        />
                        <Select
                            value={scale}
                            onChange={setScale}
                            optionList={scaleOptions}
                            style={{ width: 120 }}
                        />
                    </div>
                    <div className={styles.phoneContainer} style={{ transform: `scale(${scale})`, transformOrigin: 'top center' }}>
                        <div className={styles.deviceFrame} style={{
                            width: selectedDevice.width,
                            height: selectedDevice.height
                        }}>
                            <div className={styles.canvas} style={{ width: selectedDevice.width, height: selectedDevice.height }}>
                                <DroppableCanvas
                                    modules={modules}
                                    onDrop={handleDrop}
                                    onModuleEdit={handleStyleChange}
                                    onModuleDelete={(id) => setModules(prev => prev.filter(m => m.id !== id))}
                                    onModulesReorder={setModules}
                                    onModuleSelect={setSelectedModule}
                                />
                            </div>
                        </div>
                    </div>
                </Content>
                <Sider className={styles.rightSider}>
                    <div className={styles.propertyPanel}>
                        <PropertyEditor
                            selectedModule={selectedModule}
                            onChange={(newData) => {
                                if (!selectedModule) return;
                                updateModule(selectedModule.id, { data: newData });
                            }}
                        />
                    </div>
                </Sider>
            </Layout>
        </DndProvider>
    );
};

export default H5Editor;